
import { type Context } from "@nuxt/types";
import { ComponentOptions } from "@nuxtjs/composition-api";
import DOMPurify from "isomorphic-dompurify";
import { Component, Vue, Watch } from "nuxt-property-decorator";
import { storeToRefs } from "pinia";
import { type NavigationGuardNext, type Route } from "vue-router";

import LtkCoachmark from "@/components/Ltk/LtkCoachmark.vue";
import { useCreatorShopPostsStore } from "@/store";
import { getCollectionsStore } from "@/store/collections";
import { useCreatorProductStore } from "@/store/creator-products";
import { useCreatorShop } from "@/store/creator-shop";
import { getExperimentsStore } from "@/store/experiments";
import { getGiftGuidesStore } from "@/store/gift-guides";
import { getProductSetsStore } from "@/store/product-sets";
import { getProfilesStore } from "@/store/profiles";
import { getTrendingPostsStore } from "@/store/trending-posts";
import { formatErrorMessage, handleErrorStatusCode } from "@/utils/api";
import { encode as asciiEncode } from "@/utils/encoding/ascii";
import { encode as htmlEncode } from "@/utils/encoding/html";
import { FOUND, NOT_FOUND } from "@/utils/enums/http-codes";
import { time } from "@/utils/enums/time";
import {
  EXPERIMENTS_CREATOR_FOLLOW_VPM,
  EXPERIMENTS_CREATOR_GIFT_GUIDES,
  EXPERIMENTS_TRENDING_POST_CAROUSEL,
  FEATURE_FLAG_ACTIVE,
  FLAG_CREATOR_SHOP_SEARCH,
  VARIANT_TREATMENT,
} from "@/utils/experiments/constants";
import { COACHMARK_CREATOR_PAGE } from "@/utils/localstorage/constants";
import logger, { logLevel } from "@/utils/logger";
import { TOOLTIP_REFERRER_DISCOVER_PAGE_FOLLOW_TIMESTAMP } from "@/utils/sessionstorage/constants";

@Component({
  setup() {
    const creatorShopPostsStore = useCreatorShopPostsStore();
    const { hasPinnedPosts } = storeToRefs(creatorShopPostsStore);
    return { hasPinnedPosts };
  },
  head(this: Profile) {
    const branchMeta = {
      title: this.$t("Get my full shop experience") as string,
      title_treatment: this.$t("Get my full LTK experience") as string,
      subtitle: this.$t("Download the LTK app now to shop all of my favorite finds in one place.") as string,
      subtitle_treatment: this.$t(
        "Download the LTK app to shop my favorites and get notified when I post fresh content.",
      ) as string,
      avatarUrl: this.profile?.avatarUrl,
      button: this.$t("Get the free LTK app") as string,
      button_treatment: this.$t("Get the LTK app") as string,
      getButton: this.$t("GET") as string,
      openButton: this.$t("OPEN") as string,
      page: "creator_shop",
      continue: this.$t("Continue on web") as string,
    };

    const description = this.$t(
      "Shop the latest from {influencerName} on LTK, the easiest way to shop everything from your favorite influencers.",
      { influencerName: this.displayName },
    ) as string;

    const metaService = this.$nuxt.context.$metaService;
    const head = metaService.setPageMeta({ title: this.pageTitle, customTitle: true, description, branchMeta });

    if (this.profile) {
      const height = 400;
      const width = 400;
      const image = `${this.profile.avatarUrl}?fit=crop&w=${width}&h=${height}`;

      head.meta?.push(...metaService.setOGImg(image, width));
    }

    const displayName = asciiEncode(this.displayName);
    const bio = this.profile?.bio ? htmlEncode(this.profile?.bio) : "Shop all of my favorites right from my LTK shop!";
    const url = new URL(`https://${this.$config.ltkSite}/`);
    url.pathname = `/explore/${this.displayName}`;
    const jsonld = {
      "@context": "http://schema.org/",
      "@type": "OnlineStore",
      name: `${displayName}'s LTK Shop`,
      founder: displayName,
      description: bio,
      image: this.profile?.avatarUrl ?? "",
      url: url.href,
    };

    head.script = [{ type: "application/ld+json", json: JSON.parse(JSON.stringify(jsonld)) }];

    return head;
  },
  fetchDelay: 0,
})
export default class Profile extends Vue {
  selectedTab = "posts";
  displayName!: string;
  showCoachmark = false;
  hasPinnedPosts!: boolean;
  coachmarkTimeoutId: number | null = null;

  /**
   * Get the dynamic route parameter and set it to the component state
   */
  async asyncData({ params, error, store, redirect, $pinia }: Context) {
    const displayName = params.displayName;
    const profilesStore = getProfilesStore(store);
    const giftGuideStore = getGiftGuidesStore(store);
    const productSetStore = getProductSetsStore(store);
    const collectionsStore = getCollectionsStore(store);
    const experimentsStore = getExperimentsStore(store);
    const trendingPostStore = getTrendingPostsStore(store);
    const creatorProductStore = useCreatorProductStore($pinia); // this is necessary during SSR to ensure concurrent requests don't contaminate the store state (https://pinia.vuejs.org/core-concepts/outside-component-usage.html#SSR-Apps)

    try {
      // Ensure profile exists before rendering page
      await profilesStore.fetchProfileByDisplayName(displayName);
      const profileId = profilesStore.getIdByDisplayName(displayName);

      experimentsStore.fetchFeatureFlag({ flagName: EXPERIMENTS_CREATOR_GIFT_GUIDES });
      experimentsStore.fetchVariant({ variantName: EXPERIMENTS_CREATOR_FOLLOW_VPM });
      const giftGuideFF = experimentsStore.flag(EXPERIMENTS_CREATOR_GIFT_GUIDES).value === "on";

      if (!profileId) {
        error({ statusCode: NOT_FOUND, message: `Profile not found (${displayName})` });
        return params;
      }

      const profileDisplayName = profilesStore.profiles[profileId]?.displayName;
      if (profileDisplayName !== params.displayName) {
        redirect(FOUND, `/explore/${profileDisplayName}/`);
        return params;
      }

      // Batch additional API requests into a Promise.all, so they can run concurrently.
      const promises = [];

      if (!trendingPostStore.hasFeedBeenFetched(displayName)) {
        promises.push(trendingPostStore.fetchTrendingPostsByDisplayName({ displayName }));
      }

      if (!collectionsStore.hasFeedBeenFetched(displayName)) {
        promises.push(collectionsStore.fetchCreatorCollectionFeed({ id: profileId, displayName }));
      }

      if (giftGuideFF) {
        if (!giftGuideStore.haveCategoriesBeenFetched) {
          promises.push(giftGuideStore.fetchLocalizedCategories());
        }
        if (giftGuideStore.feeds[displayName] === undefined) {
          promises.push(
            giftGuideStore.fetchGiftGuidesByProfile({
              displayName,
              profileId,
            }),
          );
        }
      }

      if (!productSetStore.hasFeedBeenFetched(displayName)) {
        promises.push(productSetStore.fetchProductSetsByProfile({ displayName, profileId, giftGuideFF }));
      }

      promises.push(creatorProductStore.fetchCreatorTrendingProducts(profileId));
      if (experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value !== FEATURE_FLAG_ACTIVE) {
        promises.push(creatorProductStore.fetchCreatorRecentProducts(profileId));
      }

      await Promise.allSettled(promises);
    } catch (err) {
      logger(logLevel.ERROR, `Error fetching on page /explore/displayName (${params.displayName})`, {}, err);
      error({
        statusCode: handleErrorStatusCode(err),
        message: `Error fetching on page /explore/displayName (${params.displayName}) - ${formatErrorMessage(err)}`,
      });
    }
    return params;
  }

  mounted() {
    useCreatorShop().beginShopTabJourney();
    this.setSelectedTabOnMount();

    this.coachmarkTimeoutId = window.setTimeout(async () => {
      if (this.isEligibleForCoachmark) {
        if (await this.isInTooltipExperimentGroup()) {
          this.initFollowButtonCoachmark();
        }
      }
      // TS thinks setTimeout returns a NodeJS.Timeout object unsure why
      // possibly because the mounted hook runs client side instead of in Node
    }, time.TWO_SECONDS);
  }

  beforeDestroy() {
    if (this.coachmarkTimeoutId) {
      // If the coachmark timeout has an ID we should clear it
      // This prevents the coachmark from displaying after a route transition
      clearInterval(this.coachmarkTimeoutId);
    }
  }

  beforeRouteLeave(to: Route, _from: Route, next: NavigationGuardNext) {
    if (!to.name?.startsWith("explore-displayName")) {
      useCreatorShop().completeShopTabJourney();
    }
    next();
  }

  fetch() {
    const experimentsStore = getExperimentsStore(this.$store);
    experimentsStore.fetchFeatureFlag({ flagName: EXPERIMENTS_CREATOR_FOLLOW_VPM });
    experimentsStore.fetchFeatureFlag({ flagName: EXPERIMENTS_CREATOR_GIFT_GUIDES });
    experimentsStore.fetchFeatureFlag({ flagName: FLAG_CREATOR_SHOP_SEARCH });
  }

  setSelectedTabOnMount() {
    const defaultTab = "posts";
    const tab = this.$route?.query?.tab;
    const sanitizedTab = DOMPurify.sanitize(Array.isArray(tab) ? tab[0] ?? "" : tab);
    const validTabs = ["posts", "products"];

    this.selectedTab = validTabs.includes(sanitizedTab) ? sanitizedTab : defaultTab;
  }

  sendPagePublisherEvent() {
    // Prevent issues with DOM being unavailable for isEligibleForCoachmark check https://github.com/nuxt/nuxt/issues/13471
    requestAnimationFrame(() => {
      this.$amplitude?.sendEvent("/page/publisher", {
        "publisher-id": this.profile?.id ?? "",
        "display-name": this.displayName,
        "creator-shop-trending-content-carousel-displayed": this.willSeeTrendingPosts ? "true" : null,
        "creator-shop-trending-products-carousel-displayed": this.willSeeTrendingProducts ? "true" : null,
        "has-collections": this.hasCollections,
        "has-sets": this.hasProductsSets,
        "has-pinned-posts": this.hasPinnedPosts,
        "creator-shop-tab": this.selectedTab,
      });
    });
  }

  isInTooltipExperimentGroup(): boolean {
    return getExperimentsStore(this.$store).variant(EXPERIMENTS_CREATOR_FOLLOW_VPM).value === VARIANT_TREATMENT;
  }

  initFollowButtonCoachmark() {
    const coachmarkMount = document.querySelector("#coachmarkMount");
    const followButton = document.querySelector("#coachmarkTarget");
    // mount coachmark, and push button through overlay

    const Coachmark = Vue.extend(LtkCoachmark as ComponentOptions<Vue>);

    new Coachmark({
      propsData: {
        text: this.coachmarkText,
        top: "-42px",
        tailLeft: "60px",
        tailTop: "-12px",
        tailPointUp: true,
        dismissOnWindowClick: true,
        dismissOnWindowScroll: true,
        displayedInViewportDelay: time.ONE_SECOND,
        handleIsDisplayedInViewport: () => {
          try {
            localStorage.setItem(COACHMARK_CREATOR_PAGE, "true");
            // set value to enabled attribution to tooltip referrer
            sessionStorage.setItem(TOOLTIP_REFERRER_DISCOVER_PAGE_FOLLOW_TIMESTAMP, `${Date.now()}`);
          } catch (e) {
            console.error("There is an issue accessing local and/or session storage: ", e);
          }
          this.$amplitude?.sendEvent("/web-vpm-displayed", {
            "vpm-type": "follow a creator",
          });
        },
      },
    }).$mount(coachmarkMount as HTMLElement);

    followButton?.setAttribute(
      "style",
      `z-index: ${window.getComputedStyle(document.body).getPropertyValue("--z-like-and-follow-button")}`,
    );

    this.showCoachmark = true;
  }

  onPostsLoaded() {
    // This handles firing the event "onMount" as we need to delay to ensure we've fetched posts
    // This is because creatorShopPosts is a client-side store—this is getting fixed as part of BROWSE-2132
    if (process.client) {
      this.sendPagePublisherEvent();
    }
  }

  @Watch("selectedTab")
  onSelectedTabChange(selectedTab: string) {
    if (process.client) {
      window.scrollTo({ top: 0 });
      if (selectedTab !== this.$route?.query?.tab) {
        this.$router.replace({ path: this.$route.path, query: { tab: selectedTab } });
        this.sendPagePublisherEvent();
      }
      useCreatorShop().setLastSeenShopTab(selectedTab);
    }
  }

  get willSeeTrendingPosts() {
    const minTrendingPosts = 3;
    const experimentsStore = getExperimentsStore(this.$store);
    const experiment = experimentsStore.flag(EXPERIMENTS_TRENDING_POST_CAROUSEL);

    if (experiment.value !== "on") {
      return false;
    }
    return getTrendingPostsStore(this.$store).feeds[this.displayName]?.ids.length >= minTrendingPosts;
  }

  get willSeeTrendingProducts() {
    const creatorProductStore = useCreatorProductStore();
    return Boolean(creatorProductStore.trendingProductsFeed[this.profileId ?? ""]?.ids.length);
  }

  get hasProductsSets() {
    const productSetStore = getProductSetsStore(this.$store);
    return productSetStore.feeds[this.displayName]?.ids.length > 0;
  }

  get hasCollections() {
    const collectionsStore = getCollectionsStore(this.$store);
    return collectionsStore.creatorCollectionFeed(this.displayName).ids.length > 0;
  }

  get pageTitle() {
    return this.$t("{influencerName} on LTK", { influencerName: this.displayName }) as string;
  }

  get postsTabText() {
    return this.$t("Posts");
  }

  get productsTabText() {
    return this.$t("Products");
  }

  get profileId() {
    const profilesStore = getProfilesStore(this.$store);
    return profilesStore.getIdByDisplayName(this.displayName);
  }

  get profile() {
    const profilesStore = getProfilesStore(this.$store);
    return profilesStore.profiles[this.profileId ?? ""];
  }

  hasAlreadySeenCoachmark() {
    return process.client ? localStorage.getItem(COACHMARK_CREATOR_PAGE) === "true" : false;
  }

  get isEligibleForCoachmark() {
    return (
      this.$vuetify.breakpoint.xsOnly &&
      !this.hasAlreadySeenCoachmark() &&
      !this.$branch.isJourneyDisplayed() &&
      this.isFollowButtonVisible
    );
  }

  get isFollowButtonVisible() {
    if (process.client) {
      const MIN_VISIBLE_TOP_POSITION = 75;
      const followButton = document.querySelector("#coachmarkTarget");
      // We assume the DOM element is visible before destructuring
      const { top, left, bottom, right } = (followButton as HTMLElement)?.getBoundingClientRect() || {};

      return (
        top >= MIN_VISIBLE_TOP_POSITION &&
        left >= 0 &&
        bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    } else {
      return false;
    }
  }

  get coachmarkText() {
    return this.$t(
      "Tap the follow button to keep up with styles from your go-to Creators and see exclusive sales first.",
    );
  }
}
